// ── Contract review email: reviewer instructions ─────────────────────────────
// The Send for Review email used to be only the AI summary — reviewers got a
// wall of contract analysis with nothing saying what they were expected to do.
// This block goes at the top of that email.
//
// MIRRORED in cs-est-contract-api/contract-analysis/reviewInstructions.js
// (the function app builds the same email when analysis finishes server-side;
// separate deploys can't share a module). Change both together —
// src/test/contractReviewEmail.test.js pins them to identical output.
//
// One email goes to every reviewer, so the steps are generic and the reviewer
// table tells each person which row is theirs. Every value is HTML-escaped:
// project/client names and file links are user-entered and land in an email.

export const PLATFORM_BASE_URL = "https://estimating.cleanspaceus.com";

function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function fileName(url) {
  try { return decodeURIComponent(String(url).split("?")[0].split("/").pop()) || "Contract document"; }
  catch { return "Contract document"; }
}

export function buildReviewInstructionsHtml({
  turnoverId, projectName = "", clientName = "", submittedBy = "",
  reviewers = [], fileLinks = [],
} = {}) {
  const url = `${PLATFORM_BASE_URL}/turnover/${Number.parseInt(turnoverId, 10) || ""}?section=2`;
  const project = esc(projectName) || "this project";
  const td = "padding:6px 10px;border:1px solid #e5e7eb;font-size:13px;vertical-align:top";

  const reviewerRows = reviewers.map(r => `
      <tr>
        <td style="${td};font-weight:600;white-space:nowrap">${esc(r.department)}</td>
        <td style="${td}">${esc(r.name || r.email)}</td>
        <td style="${td};color:#4b5563">${esc(r.responsibility)}</td>
      </tr>`).join("");

  const files = fileLinks.filter(Boolean).map(l =>
    `<li><a href="${esc(l)}" style="color:#1d4ed8">${esc(fileName(l))}</a></li>`).join("");

  return `
<div style="font-family:Arial,sans-serif;max-width:800px;margin:0 auto 28px;color:#111">
  <div style="background:#1e3a5f;padding:16px 22px;border-radius:6px 6px 0 0">
    <div style="color:#fff;font-size:17px;font-weight:700">Action needed: contract review sign-off</div>
    <div style="color:#93c5fd;font-size:13px;margin-top:3px">${project}${clientName ? ` — ${esc(clientName)}` : ""}</div>
  </div>
  <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 6px 6px;padding:18px 22px;font-size:14px;line-height:1.55">
    <p style="margin:0 0 12px">${submittedBy ? `${esc(submittedBy)} has sent` : "This is"} the contract for <strong>${project}</strong> for department review. You are listed as a reviewer below. <strong>A job number can't be requested until every department has signed off.</strong></p>
    <ol style="margin:0 0 14px;padding-left:20px">
      <li style="margin-bottom:6px"><strong>Review the contract</strong> for your area of responsibility (your row below). The AI summary further down is a starting point to speed you up — it can miss or misread terms, so check the contract itself before you sign off.</li>
      <li style="margin-bottom:6px"><strong>Open the turnover</strong> with the button below and sign in with your CleanSpace Microsoft account. It opens on <strong>Section 2 — Contract Review &amp; Sign-Off</strong>.</li>
      <li style="margin-bottom:6px">In the <strong>Contract Review Sign-Off Matrix</strong>, find your department's row and set its <strong>Status</strong>:
        <ul style="margin:4px 0 0;padding-left:18px">
          <li><strong>Approved</strong> — nothing in your area needs to change.</li>
          <li><strong>Approved with Comments</strong> — acceptable, but note what the project team must watch for or negotiate.</li>
          <li><strong>Rejected</strong> — something must change before signing; say what in Comments.</li>
        </ul>
      </li>
      <li style="margin-bottom:6px">Type your notes in the row's <strong>Comments</strong> box — they save when you click out of it.</li>
    </ol>
    <p style="margin:0 0 16px;padding:8px 12px;background:#fef3c7;border:1px solid #fcd34d;border-radius:6px;font-size:13px">Replying to this email does <strong>not</strong> record a sign-off. Only the Status on your row in the platform counts.</p>
    <div style="margin:0 0 18px">
      <a href="${esc(url)}" style="display:inline-block;background:#1e3a5f;color:#fff;padding:10px 22px;border-radius:6px;font-size:14px;font-weight:700;text-decoration:none">Open the turnover and sign off →</a>
    </div>
    ${reviewerRows ? `
    <div style="font-size:13px;font-weight:700;margin:0 0 6px">Reviewers</div>
    <table style="border-collapse:collapse;width:100%;margin:0 0 16px">
      <tr>
        <th style="${td};background:#f8f9fb;text-align:left">Department</th>
        <th style="${td};background:#f8f9fb;text-align:left">Reviewer</th>
        <th style="${td};background:#f8f9fb;text-align:left">What to review</th>
      </tr>${reviewerRows}
    </table>` : ""}
    ${files ? `
    <div style="font-size:13px;font-weight:700;margin:0 0 6px">Contract documents</div>
    <ul style="margin:0 0 4px;padding-left:18px;font-size:13px">${files}</ul>` : ""}
  </div>
</div>`;
}
